<script lang="ts">
  import { onMount, untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { mdiContentSave, mdiRestore, mdiRefresh, mdiFilePdfBox, mdiDownload } from "@mdi/js"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import { openOverlay, closeOverlayById } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import LetterTemplateForm from "./_modules/letter-template-form"
  import CommonLetterSettingsForm from "./_modules/common-letter-settings-form"
  import LetterPreviewPanel from "./_shared/_modules/letter-preview-panel"
  import LetterPreviewOverlay from "./_modules/letter-preview-overlay"
  import {
    readLetterDraft,
    cloneDraft,
    settingsForTab,
    changedSettings,
    applySavedSettings,
    variantFields,
    defaultSettingsForVariant,
  } from "./_shared/_logic/draft"
  import { previewKey, pdfObjectUrl } from "./_logic/previewState"
  import { validateDraft, overflowMessage } from "./_logic/validation"
  import {
    previewLetterTemplate,
    updateLetterSettings,
    type LetterTemplatePreview,
  } from "../../../../../../../../api/kehrblatt/letters"
  import type { KehrblattBusinessProfile } from "../../../../../../../../api/kehrblatt/business-profile"
  import type {
    LetterEditorTab,
    LetterPreviewMode,
    LetterTemplateVariant,
  } from "./_shared/_logic/letter-types"

  let {
    organizationId,
    policy,
    profile,
    canManage,
    activeTab,
    onPolicyUpdated,
  }: {
    organizationId: string
    policy: OrganizationContactPolicy
    profile: KehrblattBusinessProfile
    canManage: boolean
    activeTab: LetterEditorTab
    onPolicyUpdated: (policy: OrganizationContactPolicy) => void
  } = $props()
  let baseline = $state(untrack(() => readLetterDraft(policy)))
  let draft = $state(untrack(() => cloneDraft(baseline)))
  let mode = $state<LetterPreviewMode>("personal")
  let saving = $state(false)
  let previewPending = $state(false)
  let error = $state<string | null>(null)
  let previewError = $state<string | null>(null)
  let notice = $state<string | null>(null)
  let preview = $state<LetterTemplatePreview | null>(null)
  let url = $state<string | null>(null)
  let renderedKey = $state("")
  let containerWidth = $state(0)
  let container: HTMLDivElement
  let generation = 0
  let active = true
  let overlayId: string | null = null
  const variant = $derived(activeTab === "other" ? "announcement" : activeTab)
  const dirty = $derived(Object.keys(changedSettings(baseline, draft)).length > 0)
  const currentKey = $derived(previewKey(draft, variant, mode, draft.letter_address_window))
  const stale = $derived(renderedKey !== currentKey)
  const disabled = $derived(saving || !canManage)

  const renderPreview = async (): Promise<LetterTemplatePreview | null> => {
    const requestGeneration = ++generation
    const key = currentKey
    const targetOrganization = organizationId
    const snapshot = cloneDraft(draft)
    previewPending = true
    previewError = null
    try {
      const result = await previewLetterTemplate(targetOrganization, snapshot, variant, mode)

      if (
        !active ||
        requestGeneration !== generation ||
        targetOrganization !== organizationId ||
        key !== currentKey
      )
        return null

      const nextUrl = pdfObjectUrl(result.pdf_base64)

      if (url) URL.revokeObjectURL(url)

      url = nextUrl
      preview = result
      renderedKey = key

      return result
    } catch {
      if (active && requestGeneration === generation && key === currentKey) {
        previewError =
          "Die Briefvorschau konnte nicht erstellt werden. Ihre Änderungen bleiben erhalten."
      }

      return null
    } finally {
      if (active && requestGeneration === generation) previewPending = false
    }
  }

  // Selection changes render a preview, but never persist settings or react to individual keystrokes.
  $effect(() => {
    const selection = [organizationId, activeTab, mode]
    untrack(() => {
      if (selection.length) void renderPreview()
    })
  })

  const save = async () => {
    if (disabled || !dirty) return

    const validation = validateDraft(draft)

    if (validation) {
      error = validation

      return
    }

    saving = true
    error = null
    notice = null
    const snapshot = cloneDraft(draft)
    const selectedTab = activeTab
    const targetOrganization = organizationId
    try {
      const result = await renderPreview()

      if (!result) {
        error = "Bitte erstellen Sie vor dem Speichern eine aktuelle Briefvorschau."

        return
      }

      const results = [result]

      if (selectedTab === "other") {
        const targets: [LetterTemplateVariant, LetterPreviewMode][] = [
          ["announcement", "personal"],
          ["announcement", "impersonal"],
          ["reschedule", "personal"],
          ["reschedule", "impersonal"],
          ["aushang", "personal"],
        ]
        for (const [targetVariant, targetMode] of targets) {
          if (targetVariant === variant && targetMode === mode) continue

          results.push(
            await previewLetterTemplate(targetOrganization, snapshot, targetVariant, targetMode),
          )
        }
      }

      if (
        !active ||
        organizationId !== targetOrganization ||
        activeTab !== selectedTab ||
        !canManage
      )
        return

      const overflow = overflowMessage(results.flatMap((item) => item.checks))

      if (overflow) {
        error = `${overflow} Die Änderung wurde nicht gespeichert.`

        return
      }

      const scope = settingsForTab(snapshot, selectedTab)
      const payload =
        selectedTab === "other"
          ? Object.fromEntries(
              Object.entries(scope).filter(
                ([key, value]) =>
                  JSON.stringify(value) !== JSON.stringify(baseline[key as keyof typeof baseline]),
              ),
            )
          : scope
      const saved = await updateLetterSettings(targetOrganization, payload)

      if (!active || organizationId !== targetOrganization) return

      const updated = applySavedSettings(policy, saved)
      baseline = readLetterDraft(updated)
      draft = cloneDraft(baseline)
      renderedKey = ""
      notice = "Änderungen gespeichert."
      onPolicyUpdated(updated)
    } catch {
      if (active)
        error =
          "Speichern ist fehlgeschlagen. Ihre Änderungen bleiben erhalten. Bitte erneut versuchen."
    } finally {
      if (active) saving = false
    }
  }

  const resetTemplate = () => {
    if (disabled || activeTab === "other") return

    const defaults = defaultSettingsForVariant(policy, activeTab)

    if (!defaults) {
      error = "Die Standardvorlage konnte nicht geladen werden. Ihr Entwurf bleibt erhalten."

      return
    }

    for (const field of variantFields(activeTab)) draft[field] = defaults[field] as string
    error = null
    notice = "Standardvorlage als Entwurf übernommen. Bitte speichern Sie die Änderung."
  }

  const showPreview = () => {
    if (overlayId) return

    if (stale) void renderPreview()

    overlayId = openOverlay({
      title: "Briefvorschau",
      content: LetterPreviewOverlay,
      defaultFullscreen: true,
      defaultMobileFullscreen: true,
      properties: {
        preview: () => ({
          url,
          pageCount: preview?.page_count ?? 0,
          stale,
          pending: previewPending,
        }),
      },
      onClose: () => {
        overlayId = null
      },
    })
  }

  const downloadAushang = async () => {
    const result = await renderPreview()

    if (!result || !active || !url) return

    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = "aushang.pdf"
    anchor.rel = "noopener noreferrer"
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  }

  onMount(() => {
    const observer = new ResizeObserver(([entry]) => {
      containerWidth = entry.contentRect.width
    })
    observer.observe(container)
    const unregister = registerNavigationGuard(
      `letter-template-${organizationId}`,
      (_to, _options, resume) => {
        if (saving) return false

        if (!dirty) return true

        void newApproval({
          title: "Ungespeicherte Briefeinstellungen",
          description: "Möchten Sie die Seite verlassen und Ihre Änderungen verwerfen?",
          approvalLabel: "Verwerfen",
          declineLabel: "Weiter bearbeiten",
          approvalCallback: () => {
            draft = cloneDraft(baseline)
            resume?.()
          },
        })

        return false
      },
    )
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty || saving) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", beforeUnload)

    return () => {
      active = false
      generation++
      observer.disconnect()
      unregister()
      window.removeEventListener("beforeunload", beforeUnload)

      if (overlayId) closeOverlayById(overlayId)

      if (url) URL.revokeObjectURL(url)
    }
  })
</script>

<section aria-label="Briefvorlagen" class="letter-section">
  <div bind:this={container} class:side-by-side={containerWidth >= 1120} class="editor">
    <div class="editor-form">
      {#if activeTab === "other"}
        <CommonLetterSettingsForm {organizationId} bind:draft {profile} {disabled} />
      {:else}
        <LetterTemplateForm bind:draft {variant} {disabled} />
      {/if}
      {#if error}<p role="alert">{error}</p>{/if}
      {#if notice}<p role="status">{notice}</p>{/if}
      <div class="save-actions">
        <CTA
          type="primaryButton"
          darkMode={false}
          icon={mdiContentSave}
          content={saving ? "Speichern …" : "Speichern"}
          onClick={save}
          disabled={disabled || !dirty}
          options={{
            label: "Briefvorlagenoptionen",
            disabled,
            items:
              activeTab !== "other"
                ? [
                    {
                      id: "default-template",
                      label: "Standardvorlage übernehmen",
                      icon: mdiRestore,
                      onClick: resetTemplate,
                    },
                  ]
                : [],
          }}
        />
      </div>
    </div>
    <aside class="preview-column">
      <h3>Briefvorschau</h3>
      <div class="form preview-controls">
        {#if variant !== "aushang"}
          <Input
            type="select"
            placeholder="Vorschau für"
            required={true}
            bind:value={mode}
            brightBackground={true}
            surfaceTone="light"
            disabled={saving}
            options={[
              { value: "personal", label: "Brief mit Empfängername" },
              { value: "impersonal", label: "Brief ohne Empfängername" },
            ]}
          />
        {/if}
        <div class="preview-actions">
          <CTA
            type="tertiaryButton"
            darkMode={false}
            small={true}
            icon={containerWidth < 1120 ? mdiFilePdfBox : mdiRefresh}
            content={containerWidth < 1120 ? "Vorschau öffnen" : "Vorschau erneut laden"}
            onClick={() => (containerWidth < 1120 ? showPreview() : void renderPreview())}
            disabled={saving || (containerWidth >= 1120 && previewPending)}
            options={{
              label: "Vorschauoptionen",
              disabled: saving,
              items: [
                ...(containerWidth < 1120
                  ? [
                      {
                        id: "refresh-preview",
                        label: "Vorschau erneut laden",
                        icon: mdiRefresh,
                        disabled: previewPending,
                        onClick: () => void renderPreview(),
                      },
                    ]
                  : []),
                ...(activeTab === "aushang"
                  ? [
                      {
                        id: "download-aushang",
                        label: "Aushang herunterladen",
                        icon: mdiDownload,
                        disabled: previewPending,
                        onClick: () => void downloadAushang(),
                      },
                    ]
                  : []),
              ],
            }}
          />
        </div>
      </div>
      {#if previewError}<p role="alert">{previewError}</p>{/if}
      {#if containerWidth >= 1120}
        <LetterPreviewPanel
          {url}
          pageCount={preview?.page_count ?? 0}
          {stale}
          pending={previewPending}
        />
      {:else}
        <p aria-live="polite"
          >{previewPending
            ? "Vorschau wird aktualisiert …"
            : stale
              ? "Vorschau nicht aktuell"
              : `Musterbrief: ${preview?.page_count ?? 0} Seite(n)`}</p
        >
      {/if}
      <p class="save-status" role="status"
        >{dirty ? "Ungespeicherte Änderungen" : "Keine ungespeicherten Änderungen"}</p
      >
    </aside>
  </div>
</section>

<style>
  .letter-section,
  .editor,
  .editor-form {
    display: grid;
    gap: 1.5rem;
    min-width: 0;
    color: var(--text-invers-100);
  }
  .side-by-side {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: start;
  }
  .side-by-side aside {
    position: sticky;
    top: var(--app-header-height, 56px);
  }
  .save-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
    flex-wrap: wrap;
  }
  .preview-actions {
    display: flex;
    gap: 0.65rem;
    flex-wrap: wrap;
  }
  .preview-column {
    display: grid;
    gap: 0.75rem;
    min-width: 0;
  }
  .preview-controls {
    display: grid;
    gap: 0.75rem;
    height: auto;
  }
  h3 {
    margin: 0;
    font-size: 1rem;
    color: var(--text-invers-100);
  }
  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.5;
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
  .letter-section :global(button.cta) {
    max-width: 100%;
  }
  .letter-section :global(button.cta p) {
    white-space: normal;
  }
</style>
